import {
	alpha,
	Box,
	keyframes,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { Plus, Sparkles, Target } from "lucide-react";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { WhiteCtaButton } from "web_myplann/Shared/Components/WhiteCtaButton";

const pulseRing = keyframes`
	0%, 100% { opacity: 1; transform: scale(1); }
	50% { opacity: 0.5; transform: scale(1.08); }
`;

type Props = {
	title: string;
	onClick?: () => void;
};

export const AddGoalCta: FC<Props> = ({ title, onClick }) => {
	const { t } = useResource();
	const theme = useTheme();
	const { palette } = theme;

	return (
		<Box px={1.6} py={1.2}>
			<Box
				borderRadius={theme.radii.lg}
				overflow="hidden"
				position="relative"
				sx={{ background: theme.gradients.premium }}
			>
				<Box
					position="absolute"
					overflow="hidden"
					sx={{ inset: 0, pointerEvents: "none" }}
				>
					<Target
						size={32}
						style={{
							position: "absolute",
							top: 12,
							left: 12,
							color: alpha(palette.darkmodeText.white, 0.07),
						}}
					/>
					<Sparkles
						size={40}
						style={{
							position: "absolute",
							bottom: 16,
							right: 12,
							color: alpha(palette.darkmodeText.white, 0.07),
						}}
					/>
				</Box>

				<Stack
					alignItems="center"
					textAlign="center"
					px={2.5}
					py={3.5}
					position="relative"
				>
					<Box position="relative" zIndex={1} mb={2}>
						<Box
							position="absolute"
							borderRadius="50%"
							border={`2px solid ${alpha(palette.darkmodeText.white, 0.3)}`}
							sx={{
								inset: 0,
								animation: `${pulseRing} 2s ease-in-out infinite`,
							}}
						/>
						<Box
							width={56}
							height={56}
							borderRadius="50%"
							bgcolor={alpha(palette.darkmodeText.white, 0.2)}
							display="flex"
							alignItems="center"
							justifyContent="center"
						>
							<Target size={28} color={palette.darkmodeText.white} />
						</Box>
					</Box>

					<Typography
						variant="subtitle3"
						color={palette.darkmodeText.white}
						mb={0.4}
					>
						{title}
					</Typography>
					<Typography
						variant="caption"
						color={alpha(palette.darkmodeText.white, 0.8)}
						mb={2.5}
					>
						{t(Resources.Goals.List.ctaDescription)}
					</Typography>

					<WhiteCtaButton onClick={onClick}>
						<Plus size={theme.iconSize.sm} />
						{t(Resources.Goals.List.ctaButton)}
					</WhiteCtaButton>
				</Stack>
			</Box>
		</Box>
	);
};
